import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Loader2, Plus, Pencil } from "lucide-react";
import { toast } from "sonner";

const CURS = ["TRY", "USD", "EUR", "GBP"];
const CUR_SYM = { TRY: "₺", USD: "$", EUR: "€", GBP: "£" };
const UNITS = ["adet", "işlem", "cm²", "m²", "metre", "kg", "dakika"];
const CATEGORIES = ["Nakış", "Baskı", "Uygulama", "Diğer"];

const fmt = (n) => Number(n || 0).toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const parseTr = (s) => {
  let t = String(s ?? "").trim().replace(/\s/g, "");
  if (!t) return NaN;
  if (t.includes(",")) t = t.replace(/\./g, "").replace(",", ".");
  else {
    const parts = t.split(".");
    if (parts.length > 2 || (parts.length === 2 && parts[1].length === 3 && parts[0] !== "0")) t = t.replace(/\./g, "");
  }
  const v = Number(t);
  return Number.isFinite(v) ? v : NaN;
};

const EMPTY = {
  name: "", code: "", category: "", unit: "adet", cost_price: "", sale_price: "",
  currency: "TRY", tax_rate: "20", description: "", is_active: true,
  send_to_production: false, production_department_id: "",
};

export default function ServicesTab({ canEdit = false }) {
  const [items, setItems] = useState([]);
  const [deptMeta, setDeptMeta] = useState({ departments: [], labels: {} });
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/services");
      setItems(data || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);
  useEffect(() => {
    api.get("/departments/meta")
      .then(({ data }) => setDeptMeta(data || { departments: [], labels: {} }))
      .catch(() => setDeptMeta({ departments: [], labels: {} }));
  }, []);

  const openNew = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (s) => {
    setEditing(s);
    setForm({
      name: s.name, code: s.code, category: s.category || "", unit: s.unit || "adet",
      cost_price: String(s.cost_price ?? "").replace(".", ","),
      sale_price: String(s.sale_price ?? "").replace(".", ","),
      currency: s.currency || "TRY", tax_rate: String(s.tax_rate ?? "").replace(".", ","),
      description: s.description || "", is_active: s.is_active !== false,
      send_to_production: Boolean(s.send_to_production),
      production_department_id: s.production_department_id || "",
    });
    setOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    const cost = parseTr(form.cost_price || "0");
    const sale = parseTr(form.sale_price || "0");
    const tax = parseTr(form.tax_rate || "0");
    if (!form.name.trim() || !form.code.trim()) { toast.error("Uygulama adı ve kod zorunlu"); return; }
    if (!Number.isFinite(cost) || !Number.isFinite(sale) || !Number.isFinite(tax)) { toast.error("Fiyat/KDV alanları geçersiz"); return; }
    if (cost < 0 || sale < 0 || tax < 0) { toast.error("Negatif değer girilemez"); return; }
    if (form.send_to_production && !form.production_department_id) {
      toast.error("Üretime gidecek uygulama için departman seçin");
      return;
    }
    setSaving(true);
    const payload = {
      name: form.name.trim(), code: form.code.trim(), category: form.category,
      unit: form.unit, cost_price: cost, sale_price: sale, currency: form.currency,
      tax_rate: tax, description: form.description,
      send_to_production: Boolean(form.send_to_production),
      production_department_id: form.send_to_production ? form.production_department_id : "",
    };
    try {
      if (editing) {
        await api.patch(`/services/${editing.id}`, payload);
        toast.success("Uygulama güncellendi");
      } else {
        await api.post("/services", payload);
        toast.success("Uygulama eklendi");
      }
      setOpen(false);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  const toggleActive = async (s) => {
    try {
      await api.patch(`/services/${s.id}`, { is_active: !(s.is_active !== false) });
      toast.success(s.is_active !== false ? "Uygulama pasife alındı" : "Uygulama aktifleştirildi");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const th = "text-[10px] uppercase font-mono text-slate-500";
  return (
    <Card className="bg-slate-900 border-slate-800 rounded-sm p-5" data-testid="services-tab">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-sm font-semibold text-slate-200">Uygulamalar / Hizmetler</h2>
          <p className="text-[11px] text-slate-500 mt-0.5">
            Nakış, baskı, transfer vb. işlemlerin merkezi fiyat ve üretim yönlendirme tanımları.
          </p>
        </div>
        {canEdit && (
          <Button size="sm" onClick={openNew} className="rounded-sm bg-blue-600 hover:bg-blue-500" data-testid="service-new-btn">
            <Plus className="w-4 h-4 mr-1" /> Yeni Uygulama
          </Button>
        )}
      </div>

      {loading ? (
        <div className="flex justify-center py-10"><Loader2 className="w-5 h-5 animate-spin text-blue-500" /></div>
      ) : items.length === 0 ? (
        <div className="text-center text-xs text-slate-500 py-10 border border-dashed border-slate-800 rounded-sm" data-testid="services-empty">
          Henüz uygulama tanımlanmadı.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[980px]">
            <thead>
              <tr className={`${th} border-b border-slate-800`}>
                <th className="text-left py-2 px-2">Kod</th>
                <th className="text-left py-2 px-2">Uygulama</th>
                <th className="text-left py-2 px-2">Kategori</th>
                <th className="text-left py-2 px-2">Birim</th>
                <th className="text-right py-2 px-2">Maliyet</th>
                <th className="text-right py-2 px-2">Satış Fiyatı</th>
                <th className="text-left py-2 px-2">PB</th>
                <th className="text-right py-2 px-2">KDV</th>
                <th className="text-left py-2 px-2">Üretim</th>
                <th className="text-center py-2 px-2">Durum</th>
                {canEdit && <th className="text-right py-2 px-2">İşlemler</th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800">
              {items.map((s) => (
                <tr key={s.id} className={s.is_active === false ? "opacity-50" : ""} data-testid={`service-row-${s.id}`}>
                  <td className="py-2 px-2 font-mono text-xs text-slate-400">{s.code}</td>
                  <td className="py-2 px-2 text-slate-200">{s.name}</td>
                  <td className="py-2 px-2 text-slate-400">{s.category || "—"}</td>
                  <td className="py-2 px-2 text-slate-400">{s.unit}</td>
                  <td className="py-2 px-2 text-right font-mono text-slate-300">{CUR_SYM[s.currency]}{fmt(s.cost_price)}</td>
                  <td className="py-2 px-2 text-right font-mono text-emerald-400 font-semibold">{CUR_SYM[s.currency]}{fmt(s.sale_price)}</td>
                  <td className="py-2 px-2 text-slate-400">{s.currency}</td>
                  <td className="py-2 px-2 text-right font-mono text-slate-400">%{Number(s.tax_rate || 0)}</td>
                  <td className="py-2 px-2" data-testid={`service-production-cell-${s.id}`}>
                    {s.send_to_production ? (
                      <span className="text-[10px] font-mono border border-orange-800 text-orange-300 rounded-sm px-1.5 py-0.5">
                        {deptMeta.labels?.[s.production_department_id] || s.production_department_id}
                      </span>
                    ) : <span className="text-slate-600">—</span>}
                  </td>
                  <td className="py-2 px-2 text-center">
                    <span className={`text-[9px] uppercase font-mono border rounded-sm px-1.5 py-0.5 ${s.is_active !== false ? "border-emerald-700 text-emerald-400" : "border-slate-700 text-slate-500"}`}
                      data-testid={`service-status-${s.id}`}>
                      {s.is_active !== false ? "Aktif" : "Pasif"}
                    </span>
                  </td>
                  {canEdit && (
                    <td className="py-2 px-2 text-right whitespace-nowrap">
                      <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-slate-400" onClick={() => openEdit(s)} data-testid={`service-edit-${s.id}`}>
                        <Pencil className="w-3.5 h-3.5" />
                      </Button>
                      <Switch
                        checked={s.is_active !== false}
                        onCheckedChange={() => toggleActive(s)}
                        className="ml-2 scale-90"
                        data-testid={`service-toggle-${s.id}`}
                      />
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-slate-900 border-slate-800 text-slate-100 rounded-sm max-w-lg" data-testid="service-dialog">
          <DialogHeader>
            <DialogTitle className="text-slate-50 tracking-tight">{editing ? "Uygulama Düzenle" : "Yeni Uygulama"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={save} className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <Label className="text-xs uppercase tracking-wider text-slate-400 font-mono">Uygulama Adı *</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="bg-slate-950 border-slate-700 rounded-sm" placeholder="örn. Nakış" data-testid="service-name-input" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-400 font-mono">Kod *</Label>
                <Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })}
                  className="bg-slate-950 border-slate-700 rounded-sm font-mono" placeholder="örn. NAKIS" data-testid="service-code-input" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-400 font-mono">Kategori</Label>
                <Select value={form.category || "none"} onValueChange={(v) => setForm({ ...form, category: v === "none" ? "" : v })}>
                  <SelectTrigger className="bg-slate-950 border-slate-700 rounded-sm" data-testid="service-category-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">—</SelectItem>
                    {CATEGORIES.map((c) => <SelectItem key={c} value={c} data-testid={`service-category-${c}`}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-400 font-mono">Birim *</Label>
                <Select value={form.unit} onValueChange={(v) => setForm({ ...form, unit: v })}>
                  <SelectTrigger className="bg-slate-950 border-slate-700 rounded-sm" data-testid="service-unit-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {UNITS.map((u) => <SelectItem key={u} value={u} data-testid={`service-unit-${u}`}>{u}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-400 font-mono">Para Birimi *</Label>
                <Select value={form.currency} onValueChange={(v) => setForm({ ...form, currency: v })}>
                  <SelectTrigger className="bg-slate-950 border-slate-700 rounded-sm" data-testid="service-currency-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CURS.map((c) => <SelectItem key={c} value={c} data-testid={`service-currency-${c}`}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-400 font-mono">Maliyet Fiyatı</Label>
                <Input type="text" inputMode="decimal" value={form.cost_price} placeholder="örn. 12,50"
                  onChange={(e) => setForm({ ...form, cost_price: e.target.value })}
                  className="bg-slate-950 border-slate-700 rounded-sm font-mono" data-testid="service-cost-input" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-400 font-mono">Satış Fiyatı *</Label>
                <Input type="text" inputMode="decimal" value={form.sale_price} placeholder="örn. 20,00"
                  onChange={(e) => setForm({ ...form, sale_price: e.target.value })}
                  className="bg-slate-950 border-slate-700 rounded-sm font-mono" data-testid="service-sale-input" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-400 font-mono">KDV %</Label>
                <Input type="text" inputMode="decimal" value={form.tax_rate}
                  onChange={(e) => setForm({ ...form, tax_rate: e.target.value })}
                  className="bg-slate-950 border-slate-700 rounded-sm font-mono" data-testid="service-tax-input" />
              </div>
              <div className="flex items-end pb-1">
                <label className="flex items-center gap-2 text-xs text-slate-300">
                  <Switch checked={form.is_active} onCheckedChange={(v) => setForm({ ...form, is_active: v })} data-testid="service-active-switch" />
                  Aktif
                </label>
              </div>
              <div className="col-span-2 border border-slate-800 rounded-sm p-3 space-y-3">
                <label className="flex items-center gap-2 text-xs text-slate-300">
                  <Switch
                    checked={form.send_to_production}
                    onCheckedChange={(v) => setForm({ ...form, send_to_production: v, production_department_id: v ? form.production_department_id : "" })}
                    data-testid="service-production-switch"
                  />
                  Üretim departmanına iş olarak gönder
                </label>
                {form.send_to_production && (
                  <div>
                    <Label className="text-xs uppercase tracking-wider text-slate-400 font-mono">Üretim Departmanı *</Label>
                    <Select value={form.production_department_id || "none"} onValueChange={(v) => setForm({ ...form, production_department_id: v === "none" ? "" : v })}>
                      <SelectTrigger className="bg-slate-950 border-slate-700 rounded-sm" data-testid="service-department-select"><SelectValue placeholder="Departman seçin" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">—</SelectItem>
                        {(deptMeta.departments || []).map((d) => (
                          <SelectItem key={d} value={d} data-testid={`service-department-${d}`}>{deptMeta.labels?.[d] || d}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>
              <div className="col-span-2">
                <Label className="text-xs uppercase tracking-wider text-slate-400 font-mono">Açıklama</Label>
                <Textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
                  className="bg-slate-950 border-slate-700 rounded-sm resize-none" data-testid="service-desc-input" />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpen(false)} className="rounded-sm">Vazgeç</Button>
              <Button type="submit" disabled={saving} className="rounded-sm bg-blue-600 hover:bg-blue-500" data-testid="service-save-btn">
                {saving && <Loader2 className="w-4 h-4 mr-1 animate-spin" />} Kaydet
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
